// Turns a face observation into a small "gaze geometry" summary: how well their
// gaze matches the camera (a percentage), how far away they are, how far their
// line of sight actually lands from the phone, and a rough estimate of where
// their head sits relative to the phone (which rides at lapel height).
//
// Everything here is APPROXIMATE and uncalibrated. Without an IMU we infer head
// elevation from the face's vertical position in the frame and the assumed field
// of view, and distance from inter-pupillary spacing. Treat the numbers as
// hints, not measurements. See src/vision/gazeChain.ts for the real research
// (appearance-based gaze, 3D gaze in the wild, PnP head pose) this approximates.

import { CONFIG } from "../config";
import type { FaceObservation } from "../types";

export interface GazeGeometry {
  /** 0..100 - how directly this person is looking at the phone (from lockScore). */
  matchPct: number;
  /** Distance to the person, metres (approximate). */
  rangeM: number;
  /** How far their line of sight lands from the phone, centimetres (0 = dead on). */
  gazeMissCm: number;
  /** Vertical offset of their head above the phone, metres (signed, + = above). */
  headAbovePhoneM: number;
  /** Rough person-height estimate, metres (phone height + head elevation + crown). */
  estHeightM: number;
}

/** Approximate eye-to-crown offset added to eye height to guess full stature. */
const EYE_TO_CROWN_M = 0.12;

/** Compute the gaze/geometry summary for one face observation. */
export function estimateGeometry(o: FaceObservation): GazeGeometry {
  const matchPct = Math.round(o.lockScore * 100);
  const rangeM = o.distanceM > 0 ? o.distanceM : 0;

  // Where their line of sight lands relative to the phone: at distance d, an
  // off-axis eye angle theta means the gaze ray passes ~ d * tan(theta) to the side.
  const offAxis = Math.hypot(o.gazeYawRad, o.gazePitchRad);
  const gazeMissCm = rangeM > 0 ? Math.max(0, Math.round(rangeM * Math.tan(offAxis) * 100)) : 0;

  // Head elevation from its vertical position in the frame: cy above centre means
  // the head is above the camera axis. Combined with distance this gives a height.
  const vFovRad = (CONFIG.distance.assumedVFovDeg * Math.PI) / 180;
  const elevationRad = (0.5 - o.cy) * vFovRad;
  const headAbovePhoneM = rangeM > 0 ? rangeM * Math.tan(elevationRad) : 0;
  const estHeightM = CONFIG.distance.phoneHeightMeters + headAbovePhoneM + EYE_TO_CROWN_M;

  return { matchPct, rangeM, gazeMissCm, headAbovePhoneM, estHeightM };
}
